// Tracks the health of the live Firestore listeners so the UI can warn
// the user when real-time data has stopped flowing — an expired/invalid
// auth token, revoked org access, or a dropped connection. Without this,
// onSnapshot errors are silent (see db.ts) and the user keeps seeing
// stale data as though it were live.
//
// Deliberately tiny: a single global "is something broken" boolean plus
// a listener set, shaped for React's useSyncExternalStore. Firestore
// reconnects transient blips on its own without firing the error
// callback, so this only flips on genuine, persistent failures.

type Listener = () => void

let hasError = false
let lastMessage = ''
const listeners = new Set<Listener>()

function emit(): void {
  for (const listener of listeners) listener()
}

export function reportConnectionError(context: string, err: unknown): void {
  const message = err instanceof Error ? err.message : String(err)
  // Always log for debugging, but only re-render when the flag flips.
  console.error('[connection] error in', context, '-', message)
  lastMessage = message
  if (hasError) return
  hasError = true
  emit()
}

// Called on every successful snapshot. Cheap no-op once healthy. A live
// snapshot from any listener means the connection is back, so clear the
// warning.
export function reportConnectionHealthy(): void {
  if (!hasError) return
  hasError = false
  lastMessage = ''
  emit()
}

export function getConnectionHasError(): boolean {
  return hasError
}

export function getConnectionLastMessage(): string {
  return lastMessage
}

export function subscribeConnectionStatus(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
